import {
  Box,
  Checkbox,
  FormControl,
  Grid,
  MenuItem,
  Select,
} from "@mui/material";
import React from "react";
import styles from "../../styles.module.scss";

import Button from "../../../../common/Button";

import { responseStatus, userStatus } from "../../../../../constants/user";
import BillUploadButton from "./BillUploadButton";
import BillUploadFields from "./BillUploadFields";
import EstimateApproval from "./EstimateApproval";
import { ESTIMATE_APPROVAL_ARRAY } from "../utils";
import EnhanceApproval from "./EnhanceApproval";

type Props = {
  modalFooterRef: React.RefObject<HTMLDivElement>;
  modalBodyWidth: number;
  selectedStatus: string;
  statusList: string[];
  patient: any;
  selectStatus: (status: string) => void;
  selectedResponseStatus: string;
  selectResponseStatus: (status: string) => void;
  uhidAssignedDate: string;
  uhid: string;
  setUhId: React.Dispatch<React.SetStateAction<string>>;
  admittedUnderDr: string;
  doctorNameHandler: (e: React.ChangeEvent<HTMLInputElement>) => void;
  ipNumber: string;
  ipNumberHandler: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setUhidAssignedDate: React.Dispatch<React.SetStateAction<string>>;
  updateStatus: () => Promise<void>;
  isDisabledSendBtn: boolean;
  hasPendingDoc: boolean;
  remark: string;
  setRemark: React.Dispatch<React.SetStateAction<string>>;
  FOLLOW_UP_ARRAY: string[];
  billSummary: IBillSummary;
  setBillSummary: React.Dispatch<React.SetStateAction<IBillSummary>>;
  stablePatientState: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
  estimateApprovalDocs: [
    { label: string; path: string }[],
    React.Dispatch<React.SetStateAction<{ label: string; path: string }[]>>
  ];
  enhanceApprovalDocs: [
    { label: string; path: string }[],
    React.Dispatch<React.SetStateAction<{ label: string; path: string }[]>>
  ];
};

export type IBillSummary = {
  billAmount: number | string;
  billNumber: string;
  billDocuments: string[];
  closedBillAmount: number | string;
};

const StickyFormSection = (props: Props) => {
  const {
    modalFooterRef,
    modalBodyWidth,
    selectedStatus,
    statusList,
    patient,
    selectStatus,
    selectedResponseStatus,
    selectResponseStatus,
    uhidAssignedDate,
    uhid,
    setUhId,
    admittedUnderDr,
    doctorNameHandler,
    ipNumber,
    ipNumberHandler,
    setUhidAssignedDate,
    updateStatus,
    isDisabledSendBtn,
    hasPendingDoc,
    remark,
    setRemark,
    FOLLOW_UP_ARRAY,
    billSummary,
    setBillSummary,
    stablePatientState,
  } = props;

  const handleBillSummary = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setBillSummary({
      ...billSummary,
      [name]: value,
    });
  };

  const handleBillUpload = (data: string[]) =>
    setBillSummary((prev: any) => ({ ...prev, billDocuments: data }));

  const [isStablePatient, setIsStablePatient] = stablePatientState;

  const isStablePatientHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsStablePatient(e.target.checked);
    selectStatus("");
  };

  return (
    <div
      ref={modalFooterRef}
      className={styles.modalPanelFooter}
      style={{ width: modalBodyWidth }}
    >
      <Grid container spacing={0}>
        <Grid item xs={12} lg={5} />
        <Grid item xs={12} lg={7}>
          <div className={styles.replyBox}>
            {ESTIMATE_APPROVAL_ARRAY.includes(patient.status) && (
              <Box>
                <Checkbox
                  checked={isStablePatient}
                  name="isStablePatient"
                  onChange={isStablePatientHandler}
                />
                <p className="form-label font-size-14 font-weight-medium cursor-pointer text-danger">
                  Stable Patient?
                </p>
              </Box>
            )}
            <div className={styles.statusInputsContainer}>
              <FormControl className={styles.dropdown} variant="standard">
                <label className="form-label">Status*</label>
                <Select
                  labelId="demo-customized-select-label"
                  id="demo-customized-select"
                  className={`form-control form-control-sm ${styles.dropdownInput}`}
                  value={selectedStatus}
                  disabled={
                    statusList.length === 0 ||
                    selectedStatus === userStatus.DRAFTED ||
                    patient.status === userStatus.QUERY_RAISED
                  }
                >
                  {statusList?.map((status: string) => {
                    return (
                      <MenuItem
                        key={status}
                        value={status}
                        className={styles.menuItem}
                        onClick={() => selectStatus(status)}
                      >
                        {status?.replaceAll("_", " ")}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              {FOLLOW_UP_ARRAY.includes(selectedStatus) && (
                <FormControl className={styles.dropdown} variant="standard">
                  <label className="form-label">Response*</label>
                  <Select
                    labelId="demo-customized-select-label"
                    id="demo-customized-select"
                    className={`form-control form-control-sm ${styles.dropdownInput}`}
                    value={selectedResponseStatus}
                  >
                    {[
                      responseStatus.RESPONDED,
                      responseStatus.NOT_RESPONDED,
                    ]?.map((status: string) => {
                      return (
                        <MenuItem
                          key={status}
                          value={status}
                          className={styles.menuItem}
                          onClick={() => selectResponseStatus(status)}
                        >
                          {status?.replaceAll("_", " ")}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              )}
              {(FOLLOW_UP_ARRAY.includes(patient.status) ||
                [userStatus.APPROVED, userStatus.ESTIMATE_APPROVED].includes(
                  patient.status
                )) &&
                selectedStatus === userStatus.ADMITTED && (
                  <>
                    {" "}
                    <div>
                      <label className="form-label">UHID*</label>
                      <input
                        value={uhid}
                        type="text"
                        onChange={(e: any) => setUhId(e.target.value)}
                        className={`form-control form-control-sm ${styles.dropdownInput}`}
                        placeholder="UHID..."
                      />
                    </div>
                    <div>
                      <label className="form-label">Admission Date*</label>
                      <input
                        value={uhidAssignedDate}
                        type="date"
                        onChange={(e: any) =>
                          setUhidAssignedDate(e.target.value)
                        }
                        className={`form-control form-control-sm ${styles.dropdownInput}`}
                        placeholder="UHID Date"
                      />
                    </div>
                  </>
                )}
            </div>
            {(FOLLOW_UP_ARRAY.includes(patient.status) ||
              [userStatus.APPROVED, userStatus.ESTIMATE_APPROVED].includes(
                patient.status
              )) &&
              selectedStatus === userStatus.ADMITTED && (
                <div className="mg-y-16">
                  <label className="form-label">Doctor's Name*</label>
                  <input
                    type="text"
                    className="form-control form-control-light form-control-sm"
                    name="name"
                    value={admittedUnderDr}
                    onChange={doctorNameHandler}
                    placeholder="Doctor's Name"
                  />
                </div>
              )}
            {selectedStatus === userStatus.ADMITTED && (
              <div className="mg-y-16">
                <label className="form-label">IP Number*</label>
                <input
                  type="text"
                  className="form-control form-control-light form-control-sm"
                  name="ipNumber"
                  value={ipNumber}
                  required
                  onChange={ipNumberHandler}
                  placeholder="IP Number"
                />
              </div>
            )}
            <EstimateApproval
              patient={patient}
              isStablePatient={isStablePatient}
              selectedStatus={selectedStatus}
              estimateApprovalDocs={props.estimateApprovalDocs}
            />

            <EnhanceApproval
              patient={patient}
              selectedStatus={selectedStatus}
              enhanceApprovalDocs={props.enhanceApprovalDocs}
            />

            <BillUploadFields
              patient={patient}
              billSummary={billSummary}
              setBillSummary={setBillSummary}
              handleBillSummary={handleBillSummary}
              handleBillUpload={handleBillUpload}
              selectedStatus={selectedStatus}
            />

            <div className={`${styles.actionWrap} mg-t-16`}>
              <div className={`${styles.replyWrap} mg-r-16`}>
                <label className="form-label">Remark*</label>
                <input
                  value={remark}
                  onChange={(e: any) => setRemark(e.target.value)}
                  type="text"
                  className="form-control form-control-light form-control-sm mb-5 "
                  name="reply"
                  placeholder="Remarks"
                  disabled={
                    statusList.length === 0 ||
                    selectedStatus === userStatus.DRAFTED ||
                    patient.status === userStatus.QUERY_RAISED
                  }
                />
              </div>
              <Button
                onClick={updateStatus}
                color="alert"
                type="submit"
                className={`btn ${styles.btnSm} ${styles.btnSubmit}`}
                disabled={isDisabledSendBtn || hasPendingDoc}
              >
                Submit
              </Button>
            </div>
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export default StickyFormSection;
